import { Accessibility, ArrowLeftRight, Dog, Lightbulb, MessageSquare } from "lucide-react";
import { useState } from "react";
import { sileo } from "sileo";
import { CardConfirmRow } from "@/components/CardConfirmRow";
import { TableExplorer } from "@/components/TableExplorer";
import { WaitlistDryRunHint } from "@/components/WaitlistDryRunHint";
import { Spinner } from "@/components/ui/spinner";
import { AccIcon, WhatsAppIcon } from "@/components/icons";
import { NoShowButton } from "@/components/NoShowButton";
import { useWaitlistSeatPicker } from "@/hooks/use-waitlist-seat-picker";
import {
  useWaitlistNoShow,
  useWaitlistNotify,
  useWaitlistRevertNoShow,
} from "@/hooks/use-waitlist";
import { FEATURE_ICONS, tableFeatureLabel } from "@/lib/table-features";
import { useAppStore } from "@/store/store";
import { dayjs } from "@/utils/date";
import type { ApiTableFeature, ApiWaitlistEntry } from "@/types/api";

// ── Relative wait time ───────────────────────────────────────────

export function formatWaitTime(createdAt: string): string {
  const mins = dayjs().diff(dayjs(createdAt), "minute");
  if (mins < 1) return "justo ahora";
  if (mins < 60) return `hace ${mins} min`;
  const hrs = Math.floor(mins / 60);
  const rem = mins % 60;
  return rem > 0 ? `hace ${hrs}h ${rem}min` : `hace ${hrs}h`;
}

// ── Resolve table ids → "Mesa 4 + Mesa 5" ────────────────────────
// Same label rule the reservation TableExplorer uses.

export function tableNamesLabel(tables: { id: string; name: string }[], ids: string[]): string {
  const byId = new Map(tables.map((t) => [t.id, t.name] as const));
  return ids
    .map((id) => {
      const name = byId.get(id) ?? "Mesa";
      return /^mesa/i.test(name) ? name : `Mesa ${name}`;
    })
    .join(" + ");
}

// ── WaitlistCard ─────────────────────────────────────────────────

export function WaitlistCard({ entry }: { entry: ApiWaitlistEntry }) {
  const tables = useAppStore((s) => s.tables);
  const setDetailWaitlistId = useAppStore((s) => s.setDetailWaitlistId);
  const noShow = useWaitlistNoShow();
  const notify = useWaitlistNotify();
  const revertNoShow = useWaitlistRevertNoShow();
  const seatPicker = useWaitlistSeatPicker(entry);
  const [confirm, setConfirm] = useState<"noshow" | null>(null);

  const { effectiveTableIds, isSeating, suggestions } = seatPicker;

  const phoneDigits = entry.guest_phone?.replace(/\D/g, "") ?? "";

  const hasPet = entry.features.includes("pet_friendly");
  const hasAccessible = entry.features.includes("accessible");
  const hasNote = Boolean(entry.notes?.trim());
  const otherFeatures = entry.features.filter((f) => f !== "pet_friendly" && f !== "accessible");

  const confirmNoShow = () => {
    noShow.mutate(entry.id, {
      onSuccess: () =>
        sileo.success({
          title: "Marcado como no-show",
          description: `${entry.guest_name} salió de la cola.`,
          button: {
            title: "Revertir",
            onClick: () =>
              revertNoShow.mutate(entry.id, {
                onSuccess: () =>
                  sileo.success({
                    title: "Devuelto a la cola",
                    description: `${entry.guest_name} sigue esperando.`,
                  }),
                onError: () =>
                  sileo.error({
                    title: "No se pudo revertir",
                    description: "Intenta de nuevo en un momento.",
                  }),
              }),
          },
        }),
      onError: () =>
        sileo.error({
          title: "No se pudo marcar",
          description: "Intenta de nuevo en un momento.",
        }),
    });
  };

  const seatLabel = effectiveTableIds ? tableNamesLabel(tables, effectiveTableIds) : null;

  return (
    // Clicking the card opens the detail overlay — same affordance as the
    // ReservationCard (a clickable div, since it nests action buttons that
    // would be invalid inside a <button>). The actions stopPropagation.
    // biome-ignore lint/a11y/useKeyWithClickEvents: mirrors ReservationCard's click-to-open pattern.
    <div
      onClick={() => setDetailWaitlistId(entry.id)}
      className="cursor-pointer rounded-[10px] border border-[#c8c8c2] bg-white p-3 transition hover:border-[#999]"
    >
      {/* Name + wait time */}
      <div className="flex items-baseline gap-2">
        <div className="min-w-0 flex-1 text-[19px] font-bold leading-tight tracking-tight">
          <span className="align-middle">{entry.guest_name}</span>
          {(hasPet || hasAccessible || hasNote) && (
            <span className="ml-1.5 inline-flex gap-1 align-middle">
              {hasPet && (
                <AccIcon>
                  <Dog className="size-3" />
                </AccIcon>
              )}
              {hasAccessible && (
                <AccIcon>
                  <Accessibility className="size-3" />
                </AccIcon>
              )}
              {hasNote && (
                <AccIcon>
                  <MessageSquare className="size-3" />
                </AccIcon>
              )}
            </span>
          )}
        </div>
        <div className="shrink-0 font-mono text-[12px] font-medium text-[#888]">
          {formatWaitTime(entry.created_at)}
        </div>
      </div>

      {/* Pax + suggested table. The suggestion chip carries a dashed amber
          border + lightbulb to read as a hint (not an assignment). The
          tiny ↔ button opens the shared TableExplorer from the list, so
          the host can pick a different mesa without entering the detail.
          Warnings (missing feature, fuera de zona, pax no entra, sobre
          cupo) live in the same boxed group so the host reads them as
          attached to the chosen mesa. */}
      <div className="mt-2 overflow-hidden rounded-lg border border-[#111] bg-white/70">
        <div className="grid grid-cols-[auto_1fr] items-stretch">
          <div className="flex flex-col gap-1 border-r border-[#c8c8c2] px-3.5 py-1.5 text-center">
            <span className="font-mono text-[9px] font-medium uppercase tracking-widest text-[#888]">
              Pax
            </span>
            <div className="flex flex-1 items-center justify-center">
              <span className="font-mono text-[20px] font-extrabold leading-none">{entry.pax}</span>
            </div>
          </div>
          <div className="flex flex-col gap-1 px-3 py-1.5">
            <span className="inline-flex items-center gap-1 font-mono text-[9px] font-medium uppercase tracking-widest text-[#8a6d00]">
              <Lightbulb className="size-3" />
              Mesa sugerida
            </span>
            <div className="flex flex-wrap items-center gap-1.5">
              {suggestions.isPending ? (
                <span className="inline-flex items-center gap-1 text-[12px] text-[#888]">
                  <Spinner className="size-3" /> Buscando…
                </span>
              ) : seatLabel ? (
                // Chip itself is the trigger to open the Explorer — tap to
                // change the suggested mesa. The ↔ glyph signals it.
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    seatPicker.openExplorer();
                  }}
                  className="inline-flex cursor-pointer items-center gap-1.5 rounded border border-dashed border-[#b88a00] bg-[#fffbeb] px-2 py-0.5 font-mono text-[15px] font-bold leading-none text-[#8a6d00] transition hover:border-[#8a6d00] hover:bg-[#fff5d4]"
                  aria-label={`Cambiar ${seatLabel}`}
                >
                  {seatLabel}
                  <ArrowLeftRight className="size-3 opacity-70" />
                </button>
              ) : (
                // No auto-fit suggestion: still let the host open the
                // Explorer to pick (or redistribute) a table manually.
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    seatPicker.openExplorer();
                  }}
                  className="inline-flex cursor-pointer items-center gap-1.5 rounded border border-dashed border-[#b88a00] bg-[#fffbeb] px-2 py-0.5 font-mono text-[15px] font-bold leading-none text-[#8a6d00] transition hover:border-[#8a6d00] hover:bg-[#fff5d4]"
                  aria-label="Elegir mesa manualmente"
                >
                  Sin mesa libre
                  <ArrowLeftRight className="size-3 opacity-70" />
                </button>
              )}
            </div>
            {effectiveTableIds && (
              <WaitlistDryRunHint
                entry={entry}
                tableIds={effectiveTableIds}
                startsAt={seatPicker.seatWindow.startsAt}
                endsAt={seatPicker.seatWindow.endsAt}
              />
            )}
          </div>
        </div>
      </div>

      {/* Extra features (high_chair / outdoor_view / quiet_zone). Zone
          lives in the detail panel only — keeping the card focused on
          the seat affordance. */}
      {otherFeatures.length > 0 && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          {otherFeatures.map((f) => {
            const Icon = FEATURE_ICONS[f as ApiTableFeature];
            return (
              <span
                key={f}
                className="inline-flex items-center gap-1 rounded-full border border-[#c8c8c2] bg-[#f7f6f3] px-2 py-0.5 text-[11px] text-[#555]"
              >
                {Icon && <Icon className="size-3" />}
                {tableFeatureLabel(f as ApiTableFeature)}
              </span>
            );
          })}
        </div>
      )}

      {/* Notes live only in the detail view — the queue card stays
          dense and uses the click-to-open affordance for anything
          beyond name + pax + perks + actions. */}

      {/* Actions — split into two steps to match the host's real flow:
              waiting   → "Avisar" tells the guest the table is ready
                          (guest's page swaps to "tu mesa está lista").
              notified  → "Sentar" creates the walk-in reservation once
                          the guest physically arrives.
          No-show stays available in both, as an inline confirm row
          (destructive). */}
      {confirm === null && (
        <div className="mt-2 flex items-stretch gap-2">
          {entry.status === "notified" ? (
            <button
              type="button"
              disabled={!effectiveTableIds || isSeating}
              onClick={(e) => {
                e.stopPropagation();
                if (effectiveTableIds) seatPicker.seatNow(effectiveTableIds);
              }}
              className="flex min-h-9 flex-1 items-center justify-center rounded-lg bg-[#111] px-2.5 text-xs font-semibold text-white disabled:opacity-40"
            >
              Sentar
            </button>
          ) : (
            <button
              type="button"
              disabled={!effectiveTableIds || notify.isPending}
              onClick={(e) => {
                e.stopPropagation();
                notify.mutate(entry.id);
              }}
              className="flex min-h-9 flex-1 items-center justify-center rounded-lg bg-emerald-600 px-2.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-40"
            >
              Avisar
            </button>
          )}
          <NoShowButton
            className="px-3.5"
            disabled={noShow.isPending}
            onClick={(e) => {
              e.stopPropagation();
              setConfirm("noshow");
            }}
          >
            No-show
          </NoShowButton>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (phoneDigits) window.open(`https://wa.me/${phoneDigits}`, "_blank", "noopener");
            }}
            disabled={!phoneDigits}
            aria-label="Contactar por WhatsApp"
            className="flex min-h-9 w-11 shrink-0 items-center justify-center rounded-lg border border-[#c8c8c2] bg-white disabled:opacity-40"
          >
            <WhatsAppIcon />
          </button>
        </div>
      )}

      {confirm === "noshow" && (
        <CardConfirmRow
          tone="warn"
          label="¿Confirmar No-Show?"
          yesLabel="Sí, no-show"
          onYes={(e) => {
            e.stopPropagation();
            confirmNoShow();
            setConfirm(null);
          }}
          onNo={(e) => {
            e.stopPropagation();
            setConfirm(null);
          }}
        />
      )}

      {/* TableExplorer drawer mounted at card level so 'Cambiar' opens
          the same picker the detail view uses. The Sheet portals to
          body, but React events still bubble through the virtual tree
          — wrap with stopPropagation so picking an option inside the
          drawer doesn't also fire the card's openDetail handler. */}
      {/** biome-ignore lint/a11y/useKeyWithClickEvents: pure event guard, not a control. */}
      <div onClick={(e) => e.stopPropagation()}>
        <TableExplorer {...seatPicker.explorerProps} />
      </div>
    </div>
  );
}
